import { UserAction } from '../../insights/userActionEnum.js';
import { asInstant, optionalString, requireInstant, requireNumber, requireObject, requireString } from './parse.js';

/**
 * List endpoints on the insights page currently throw when the body is not an array.
 * Unlike identity lists, null must not become [].
 *
 * @template T
 * @param {unknown} raw
 * @param {string} typeName
 * @param {(item: object) => T} mapper
 * @returns {T[]}
 */
function requireDtoList(raw, typeName, mapper) {
  if (!Array.isArray(raw)) {
    throw new Error(`${typeName}[]: expected an array`);
  }
  return raw.map(mapper);
}

/** Jackson Duration stays an ISO-8601 string such as PT6H9M. */
function asDuration(value, typeName, field, required) {
  if (value == null) {
    if (required) {
      throw new Error(`${typeName}: ${field} is required`);
    }
    return null;
  }
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${typeName}: ${field} must be an ISO-8601 duration`);
  }
  return value;
}

function requireUserAction(key, typeName) {
  if (!Object.values(UserAction).includes(key)) {
    throw new Error(`${typeName}: unknown UserAction "${key}"`);
  }
  return key;
}

function fromUserActionCountMap(raw, typeName) {
  if (raw == null) {
    return {};
  }
  const source = requireObject(raw, typeName);
  const counts = {};
  Object.entries(source).forEach(([key, count]) => {
    const action = requireUserAction(key, typeName);
    if (typeof count !== 'number') {
      throw new Error(`${typeName}: ${action} count must be a number`);
    }
    counts[action] = count;
  });
  return counts;
}

function fromUserEventAggregate(raw) {
  const source = requireObject(raw, 'UserEventAggregate');
  return {
    userId: requireString(source, 'userId', 'UserEventAggregate'),
    count: requireNumber(source, 'count', 'UserEventAggregate'),
  };
}

function fromUserActionAggregateMap(raw, typeName) {
  if (raw == null) {
    return {};
  }
  const source = requireObject(raw, typeName);
  const aggregates = {};
  Object.entries(source).forEach(([key, value]) => {
    const action = requireUserAction(key, typeName);
    aggregates[action] = requireDtoList(value, 'UserEventAggregate', fromUserEventAggregate);
  });
  return aggregates;
}

/**
 * Mirrors com.medprompt.insights.snapshot.DurationSnapshot.
 * Absent maps stay empty; Jackson omits them when null.
 *
 * @param {object} raw
 */
export function fromDurationSnapshot(raw) {
  const source = requireObject(raw, 'DurationSnapshot');
  return {
    userActionToCount: fromUserActionCountMap(source.userActionToCount, 'DurationSnapshot'),
    userActionToAggregate: fromUserActionAggregateMap(source.userActionToAggregate, 'DurationSnapshot'),
  };
}

/**
 * Mirrors com.medprompt.insights.document.snapshot.ProcessorStateSnapshot.
 *
 * @param {object} raw
 */
export function fromProcessorStateSnapshot(raw) {
  const source = requireObject(raw, 'ProcessorStateSnapshot');
  return {
    documentsReadyForReview: requireNumber(source, 'documentsReadyForReview', 'ProcessorStateSnapshot'),
    documentsNeverOpened: requireNumber(source, 'documentsNeverOpened', 'ProcessorStateSnapshot'),
    documentsAssigned: requireNumber(source, 'documentsAssigned', 'ProcessorStateSnapshot'),
    totalDocumentsOpenedByUser: requireNumber(source, 'totalDocumentsOpenedByUser', 'ProcessorStateSnapshot'),
  };
}

/**
 * Mirrors com.medprompt.insights.document.snapshot.ProcessorSnapshot.
 *
 * @param {object} raw
 */
export function fromProcessorSnapshot(raw) {
  const source = requireObject(raw, 'ProcessorSnapshot');
  return {
    timestamp: requireInstant(source, 'timestamp', 'ProcessorSnapshot'),
    duration: asDuration(source.duration, 'ProcessorSnapshot', 'duration', true),
    storageSnapshot: fromProcessorStateSnapshot(source.storageSnapshot),
    durationSnapshot: fromDurationSnapshot(source.durationSnapshot),
  };
}

/** @param {unknown} raw */
export function fromProcessorSnapshotList(raw) {
  return requireDtoList(raw, 'ProcessorSnapshot', fromProcessorSnapshot);
}

/**
 * Mirrors com.medprompt.insights.task.snapshot.TasksStateSnapshot.
 *
 * @param {object} raw
 */
export function fromTasksStateSnapshot(raw) {
  const source = requireObject(raw, 'TasksStateSnapshot');
  return {
    incompleteTasks: requireNumber(source, 'incompleteTasks', 'TasksStateSnapshot'),
    tasksNeverOpened: requireNumber(source, 'tasksNeverOpened', 'TasksStateSnapshot'),
    tasksAssigned: requireNumber(source, 'tasksAssigned', 'TasksStateSnapshot'),
    totalTasksOpenedByUser: requireNumber(source, 'totalTasksOpenedByUser', 'TasksStateSnapshot'),
  };
}

/**
 * Mirrors com.medprompt.insights.task.snapshot.TasksSnapshot.
 * The state object is `stateSnapshot`, not `storageSnapshot`.
 *
 * @param {object} raw
 */
export function fromTasksSnapshot(raw) {
  const source = requireObject(raw, 'TasksSnapshot');
  return {
    timestamp: requireInstant(source, 'timestamp', 'TasksSnapshot'),
    duration: asDuration(source.duration, 'TasksSnapshot', 'duration', true),
    stateSnapshot: fromTasksStateSnapshot(source.stateSnapshot),
    durationSnapshot: fromDurationSnapshot(source.durationSnapshot),
  };
}

/** @param {unknown} raw */
export function fromTasksSnapshotList(raw) {
  return requireDtoList(raw, 'TasksSnapshot', fromTasksSnapshot);
}

/**
 * Mirrors com.medprompt.insights.consultation.snapshot.ConsultationStateSnapshot.
 *
 * @param {object} raw
 */
export function fromConsultationStateSnapshot(raw) {
  const source = requireObject(raw, 'ConsultationStateSnapshot');
  return {
    consultationsReadyForReview: requireNumber(source, 'consultationsReadyForReview', 'ConsultationStateSnapshot'),
  };
}

/**
 * Mirrors com.medprompt.insights.consultation.snapshot.ConsultationSnapshot.
 *
 * @param {object} raw
 */
export function fromConsultationSnapshot(raw) {
  const source = requireObject(raw, 'ConsultationSnapshot');
  return {
    timestamp: requireInstant(source, 'timestamp', 'ConsultationSnapshot'),
    duration: asDuration(source.duration, 'ConsultationSnapshot', 'duration', true),
    storageSnapshot: fromConsultationStateSnapshot(source.storageSnapshot),
    durationSnapshot: fromDurationSnapshot(source.durationSnapshot),
  };
}

/** @param {unknown} raw */
export function fromConsultationSnapshotList(raw) {
  return requireDtoList(raw, 'ConsultationSnapshot', fromConsultationSnapshot);
}

/**
 * Mirrors com.medprompt.insights.document.review.DocumentReviewSummary.
 *
 * @param {object} raw
 */
export function fromDocumentReviewSummary(raw) {
  const source = requireObject(raw, 'DocumentReviewSummary');
  return {
    documentId: requireString(source, 'documentId', 'DocumentReviewSummary'),
    readyForReviewAt: asInstant(source.readyForReviewAt),
    timeToOpen: asDuration(source.timeToOpen, 'DocumentReviewSummary', 'timeToOpen', false),
    timeToReview: asDuration(source.timeToReview, 'DocumentReviewSummary', 'timeToReview', false),
    timeToFileAway: asDuration(source.timeToFileAway, 'DocumentReviewSummary', 'timeToFileAway', false),
  };
}

/**
 * Mirrors com.medprompt.insights.document.review.DocumentsReviewSummary.
 *
 * @param {object} raw
 */
export function fromDocumentsReviewSummary(raw) {
  const source = requireObject(raw, 'DocumentsReviewSummary');
  return {
    documentsReviewSummary: requireDtoList(source.documentsReviewSummary, 'DocumentReviewSummary', fromDocumentReviewSummary),
    averageTimeToOpen: asDuration(source.averageTimeToOpen, 'DocumentsReviewSummary', 'averageTimeToOpen', false),
    averageTimeToReview: asDuration(source.averageTimeToReview, 'DocumentsReviewSummary', 'averageTimeToReview', false),
    averageTimeToFileAway: asDuration(source.averageTimeToFileAway, 'DocumentsReviewSummary', 'averageTimeToFileAway', false),
  };
}

/**
 * Mirrors com.medprompt.insights.document.review.DocumentsReviewAggregate.
 *
 * @param {object} raw
 */
export function fromDocumentsReviewAggregate(raw) {
  const source = requireObject(raw, 'DocumentsReviewAggregate');
  return {
    userId: requireString(source, 'userId', 'DocumentsReviewAggregate'),
    totalFiledAway: requireNumber(source, 'totalFiledAway', 'DocumentsReviewAggregate'),
    totalTimeInReview: asDuration(source.totalTimeInReview, 'DocumentsReviewAggregate', 'totalTimeInReview', true),
    totalAssigned: requireNumber(source, 'totalAssigned', 'DocumentsReviewAggregate'),
    totalTimeAssigning: asDuration(source.totalTimeAssigning, 'DocumentsReviewAggregate', 'totalTimeAssigning', true),
    totalWorked: requireNumber(source, 'totalWorked', 'DocumentsReviewAggregate'),
  };
}

/** @param {unknown} raw */
export function fromDocumentsReviewAggregateList(raw) {
  return requireDtoList(raw, 'DocumentsReviewAggregate', fromDocumentsReviewAggregate);
}

/**
 * Mirrors com.medprompt.insights.consultation.review.ConsultationReviewSummary.
 *
 * @param {object} raw
 */
export function fromConsultationReviewSummary(raw) {
  const source = requireObject(raw, 'ConsultationReviewSummary');
  return {
    consultationId: requireString(source, 'consultationId', 'ConsultationReviewSummary'),
    recordingDuration: asDuration(source.recordingDuration, 'ConsultationReviewSummary', 'recordingDuration', false),
    recordingCompletedAt: asInstant(source.recordingCompletedAt),
    timeToReview: asDuration(source.timeToReview, 'ConsultationReviewSummary', 'timeToReview', false),
  };
}

/**
 * Mirrors com.medprompt.insights.consultation.review.ConsultationsReviewSummary.
 *
 * @param {object} raw
 */
export function fromConsultationsReviewSummary(raw) {
  const source = requireObject(raw, 'ConsultationsReviewSummary');
  return {
    consultationsReviewSummary: requireDtoList(
      source.consultationsReviewSummary,
      'ConsultationReviewSummary',
      fromConsultationReviewSummary
    ),
    averageRecordingDuration: asDuration(source.averageRecordingDuration, 'ConsultationsReviewSummary', 'averageRecordingDuration', false),
    averageTimeToReview: asDuration(source.averageTimeToReview, 'ConsultationsReviewSummary', 'averageTimeToReview', false),
  };
}

/**
 * Mirrors com.medprompt.insights.consultation.review.ConsultationsReviewAggregate.
 *
 * @param {object} raw
 */
export function fromConsultationsReviewAggregate(raw) {
  const source = requireObject(raw, 'ConsultationsReviewAggregate');
  return {
    userId: requireString(source, 'userId', 'ConsultationsReviewAggregate'),
    totalEncounters: requireNumber(source, 'totalEncounters', 'ConsultationsReviewAggregate'),
    totalTimeInEncounters: asDuration(source.totalTimeInEncounters, 'ConsultationsReviewAggregate', 'totalTimeInEncounters', true),
  };
}

/** @param {unknown} raw */
export function fromConsultationsReviewAggregateList(raw) {
  return requireDtoList(raw, 'ConsultationsReviewAggregate', fromConsultationsReviewAggregate);
}

/**
 * Mirrors com.medprompt.document.DocumentAssigned.
 *
 * @param {object} raw
 */
export function fromDocumentAssigned(raw) {
  const source = requireObject(raw, 'DocumentAssigned');
  return {
    documentId: requireString(source, 'documentId', 'DocumentAssigned'),
    assigneeId: optionalString(source.assigneeId),
    timestamp: requireNumber(source, 'timestamp', 'DocumentAssigned'),
  };
}

/**
 * Mirrors com.medprompt.insights.notification.DocumentAssignedSummary.
 *
 * @param {object} raw
 */
export function fromDocumentAssignedSummary(raw) {
  const source = requireObject(raw, 'DocumentAssignedSummary');
  return {
    documentsAssigned: requireDtoList(source.documentsAssigned, 'DocumentAssigned', fromDocumentAssigned),
    missingCount: requireNumber(source, 'missingCount', 'DocumentAssignedSummary'),
  };
}
